import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { ChatService } from '../../services/chat.service';
import { AdminFeedbackItem } from '../../interfaces/feedback.interface';
import { ChatHistoryMessage } from '../../interfaces/chat-history-response.interface';
import { GetChatHistoryByChatIdModel } from '../../interfaces/get-chat-history-by-chat-id.interface';
import { MarkdownPipe } from '../../../../shared/pipes/markdown.pipe';

@Component({
  selector: 'app-feedback-admin',
  standalone: true,
  imports: [CommonModule, MarkdownPipe],
  templateUrl: './feedback-admin.component.html',
  styleUrls: ['./feedback-admin.component.css']
})
export class FeedbackAdminComponent implements OnInit, OnDestroy {
  @Output() backClick = new EventEmitter<void>();
  @ViewChild('feedbackModalBody') feedbackModalBody?: ElementRef<HTMLDivElement>;
  @ViewChild('historyModalBody') historyModalBody?: ElementRef<HTMLDivElement>;

  newFeedbacks: AdminFeedbackItem[] = [];
  resolvedFeedbacks: AdminFeedbackItem[] = [];
  isLoading = true;
  error: string | null = null;
  resolvingKey: string | null = null;
  selectedFeedback: AdminFeedbackItem | null = null;
  chatHistoryMessages: ChatHistoryMessage[] = [];
  chatHistoryFeedbacks: AdminFeedbackItem[] = [];
  selectedHistoryFeedback: AdminFeedbackItem | null = null;
  viewMode: 'feedback' | 'history' = 'feedback';
  isLoadingChatHistory = false;
  chatHistoryError: string | null = null;
  isNewFeedbacksOpen = true;
  isResolvedFeedbacksOpen = true;

  private subscriptions = new Subscription();

  constructor(private chatService: ChatService) {}

  ngOnInit(): void {
    this.loadFeedbacks();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadFeedbacks(): void {
    this.isLoading = true;
    this.error = null;

    this.subscriptions.add(
      this.chatService.getAllFeedbacks().subscribe({
        next: (response) => {
          const feedbacks = response?.data?.feedbacks ?? [];
          const sortedFeedbacks = [...feedbacks].sort((a, b) =>
            new Date(this.getSortDate(b)).getTime() - new Date(this.getSortDate(a)).getTime()
          );

          this.newFeedbacks = sortedFeedbacks.filter(item => !item.feedback?.isResolved);
          this.resolvedFeedbacks = sortedFeedbacks.filter(item => !!item.feedback?.isResolved);
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading feedbacks:', error);
          this.error = 'Failed to load feedbacks';
          this.isLoading = false;
        }
      })
    );
  }

  toggleSection(section: 'new' | 'resolved'): void {
    if (section === 'new') {
      this.isNewFeedbacksOpen = !this.isNewFeedbacksOpen;
      return;
    }

    this.isResolvedFeedbacksOpen = !this.isResolvedFeedbacksOpen;
  }

  resolveFeedback(item: AdminFeedbackItem): void {
    const feedbackKey = this.getFeedbackKey(item);
    if (this.resolvingKey === feedbackKey) {
      return;
    }

    this.resolvingKey = feedbackKey;

    this.subscriptions.add(
      this.chatService.resolveFeedback({
        chatId: item.chatId,
        messageIndex: item.messageIndex
      }).subscribe({
        next: () => {
          this.resolvingKey = null;
          this.selectedFeedback = null;
          this.loadFeedbacks();
        },
        error: (error) => {
          console.error('Error resolving feedback:', error);
          this.error = 'Failed to resolve feedback';
          this.resolvingKey = null;
        }
      })
    );
  }

  openChatHistory(): void {
    if (!this.selectedFeedback) {
      return;
    }

    this.viewMode = 'history';
    this.selectedHistoryFeedback = this.selectedFeedback;
    this.chatHistoryError = null;
    this.isLoadingChatHistory = true;
    this.chatHistoryMessages = [];
    this.chatHistoryFeedbacks = this.getFeedbacksForChat(this.selectedFeedback.chatId);

    const request: GetChatHistoryByChatIdModel = {
      chatId: this.selectedFeedback.chatId
    };

    this.subscriptions.add(
      this.chatService.getChatHistoryByChatId(request).subscribe({
        next: (response) => {
          const messages = this.parseChatHistoryMessages(response?.data?.messagesJson);
          this.chatHistoryMessages = messages;
          this.isLoadingChatHistory = false;
          this.scrollSelectedHistoryMessageToTop();
        },
        error: (error) => {
          console.error('Error loading chat history for feedback:', error);
          this.chatHistoryError = 'Failed to load chat history';
          this.isLoadingChatHistory = false;
        }
      })
    );
  }

  backToFeedbackDetails(): void {
    this.viewMode = 'feedback';
    this.chatHistoryError = null;
  }

  selectHistoryFeedback(item: AdminFeedbackItem): void {
    this.selectedHistoryFeedback = item;
    this.scrollSelectedHistoryMessageToTop();
  }

  onBackClick(): void {
    this.backClick.emit();
  }

  openFeedbackDetails(item: AdminFeedbackItem): void {
    this.selectedFeedback = item;
    this.viewMode = 'feedback';
    this.selectedHistoryFeedback = null;
    this.chatHistoryMessages = [];
    this.chatHistoryFeedbacks = [];
    this.chatHistoryError = null;
    this.scrollModalBodyToTop('feedback');
  }

  closeFeedbackDetails(): void {
    this.selectedFeedback = null;
    this.selectedHistoryFeedback = null;
    this.chatHistoryMessages = [];
    this.chatHistoryFeedbacks = [];
    this.chatHistoryError = null;
    this.viewMode = 'feedback';
  }

  trackByFeedback = (_: number, item: AdminFeedbackItem): string =>
  `${item.chatId}_${item.messageIndex}`;

  getSortDate(item: AdminFeedbackItem): string {
    return item.feedback?.submittedAt || item.messageCreatedDate;
  }

  getFeedbackKey(item: AdminFeedbackItem): string {
    return `${item.chatId}_${item.messageIndex}`;
  }

  getFeedbackTone(item: AdminFeedbackItem): string {
    if (item.feedback?.isLiked === true) {
      return 'Positive';
    }

    if (item.feedback?.isLiked === false) {
      return 'Negative';
    }

    return 'Neutral';
  }

  getFeedbackPreview(item: AdminFeedbackItem): string {
    const comment = this.formatDisplayText(item.feedback?.comment, 'No written comment provided.');
    if (comment !== 'No written comment provided.') {
      return comment;
    }

    return this.formatDisplayText(item.userMessage, 'No prompt available.');
  }

  getFeedbackCountForChat(chatId: string): number {
    return this.getFeedbacksForChat(chatId).length;
  }

  getFeedbacksForChat(chatId: string): AdminFeedbackItem[] {
    return [...this.newFeedbacks, ...this.resolvedFeedbacks]
      .filter(item => item.chatId === chatId)
      .sort((a, b) => a.messageIndex - b.messageIndex);
  }

  getVisibleChatHistoryMessages(): ChatHistoryMessage[] {
    if (!this.selectedHistoryFeedback) {
      return this.chatHistoryMessages;
    }

    return this.chatHistoryMessages.slice(0, this.selectedHistoryFeedback.messageIndex + 1);
  }

  getFeedbackRating(item: AdminFeedbackItem): number {
    const comment = item.feedback?.comment || '';
    const match = comment.match(/Rating:\s*(\d)\s*\/\s*5/i);
    if (match) {
      const parsed = Number(match[1]);
      if (!Number.isNaN(parsed)) {
        return Math.max(0, Math.min(5, parsed));
      }
    }

    if (item.feedback?.isLiked === true) {
      return 5;
    }

    if (item.feedback?.isLiked === false) {
      return 1;
    }

    return 0;
  }

  getStars(): number[] {
    return Array.from({ length: 5 }, (_, index) => index + 1);
  }

  isStarFilled(star: number, rating: number): boolean {
    return star <= rating;
  }

  private scrollModalBodyToTop(mode: 'feedback' | 'history'): void {
    setTimeout(() => {
      const container = mode === 'feedback'
        ? this.feedbackModalBody?.nativeElement
        : this.historyModalBody?.nativeElement;

      if (container) {
        container.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 0);
  }

  private scrollSelectedHistoryMessageToTop(): void {
    setTimeout(() => {
      const container = this.historyModalBody?.nativeElement;
      const feedbackIndex = this.selectedHistoryFeedback?.messageIndex;

      if (!container || feedbackIndex === undefined || feedbackIndex === null) {
        return;
      }

      const target = container.querySelector(`[data-history-feedback-index="${feedbackIndex}"]`) as HTMLElement | null;
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }

  private parseChatHistoryMessages(messagesJson: string | undefined | null): ChatHistoryMessage[] {
    if (!messagesJson) {
      return [];
    }

    try {
      const messages = JSON.parse(messagesJson);
      return messages.map((msg: any) => ({
        UserMessage: msg.UserMessage,
        AIResponse: msg.AIResponse,
        CreatedDate: msg.CreatedDate,
        Feedback: msg.Feedback ? {
          isLiked: msg.Feedback.IsLiked,
          comment: msg.Feedback.Comment
        } : null
      }));
    } catch (error) {
      console.error('Error parsing chat history messages:', error);
      return [];
    }
  }

  formatDisplayText(value: string | null | undefined, fallback = 'No written comment provided.'): string {
    if (!value) {
      return fallback;
    }

    if (typeof DOMParser === 'undefined') {
      return value.trim() || fallback;
    }

    const normalizedValue = value
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<\/div>/gi, '\n')
      .replace(/<\/li>/gi, '\n')
      .replace(/<li>/gi, '• ');

    const parsed = new DOMParser().parseFromString(normalizedValue, 'text/html');
    const text = parsed.documentElement.textContent ?? '';
    const markdownCleaned = this.stripMarkdownTokens(text);

    return markdownCleaned
      .replace(/\r\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim() || fallback;
  }

  private stripMarkdownTokens(text: string): string {
    return text
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/__(.*?)__/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/_(.*?)_/g, '$1')
      .replace(/`([^`]+)`/g, '$1');
  }
}
